import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { goToLogout } from "@/auth/sso";
import Icon from "@/components/Icon";

/**
 * Menu de compte : bouton « avatar + nom » qui ouvre un menu déroulant regroupant
 * les options rattachées à l'utilisateur (espace enseignant, mon compte, déconnexion). Sépare
 * clairement le compte des autres éléments de la barre (cf. retour UX).
 */
export default function UserMenu() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Exclusivité avec le lanceur d'apps (cf. AppLauncher) : un seul panneau ouvert.
  useEffect(() => {
    if (open) document.dispatchEvent(new CustomEvent("orense:menu-open", { detail: "usermenu" }));
  }, [open]);
  useEffect(() => {
    const onOther = (e: Event) => { if ((e as CustomEvent).detail !== "usermenu") setOpen(false); };
    document.addEventListener("orense:menu-open", onOther);
    return () => document.removeEventListener("orense:menu-open", onOther);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const name    = user?.username ?? "";
  const initial = name ? name[0].toUpperCase() : "?";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <style>{`.pl-acct-item:hover:not(:disabled){background:var(--bg-base);}`}</style>
      <button
        type="button"
        style={styles.trigger}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Compte"
      >
        <span style={styles.avatar}>{initial}</span>
        <span style={styles.name}>{name}</span>
        <Icon name={open ? "expanded" : "collapsed"} size={14} style={{ color: "var(--text-muted)" }} />
      </button>

      {open && (
        <div style={styles.menu} role="menu">
          {user?.is_instructor && (
            <button
              type="button"
              className="pl-acct-item"
              style={styles.item}
              role="menuitem"
              onClick={() => { setOpen(false); navigate("/instructor"); }}
            >
              <Icon name="panel" size={15} style={{ color: "var(--orange)" }} />
              Espace enseignant
            </button>
          )}
          <button
            type="button"
            className="pl-acct-item"
            style={styles.item}
            role="menuitem"
            onClick={() => { setOpen(false); window.location.href = "https://compte.orense.ch/mon-compte"; }}
          >
            <Icon name="locked" size={15} style={{ color: "var(--text-muted)" }} />
            Mon compte
          </button>
          <button
            type="button"
            className="pl-acct-item"
            style={styles.item}
            role="menuitem"
            onClick={() => { setOpen(false); goToLogout(); }}
          >
            <Icon name="back" size={15} style={{ color: "var(--text-muted)" }} />
            Déconnexion
          </button>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  trigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    padding: "4px 10px 4px 4px",
    color: "var(--text)",
    cursor: "pointer",
    fontSize: 13,
  },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: "var(--blue)",
    color: "var(--on-accent)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 13,
    flexShrink: 0,
  },
  name: { color: "var(--text)", fontWeight: 600, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  menu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    right: 0,
    minWidth: 190,
    background: "var(--bg-panel)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    boxShadow: "var(--shadow-1)",
    padding: 4,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    zIndex: 60,
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderRadius: "var(--radius-sm)",
    color: "var(--text)",
    padding: "8px 10px",
    cursor: "pointer",
    fontSize: 13,
    whiteSpace: "nowrap",
  },
};
